import { computed, reactive, ref } from 'vue'
import { resolveSeasonalRow, sumBreakdown } from '~~/shared/quote'

export function formatPackageTier(value) {
    const labels = {
        standard: 'Standard',
        comfort: 'Classic Comfort',
        deluxe: 'Premium Deluxe',
        luxury: 'Luxury Resort',
    }

    if (labels[value])
        return labels[value]

    return value.replaceAll('-', ' ').replace(/\b\w/g, letter => letter.toUpperCase())
}

function resolveChildPrice(row) {
    return row.price_child === '' || row.price_child === null || row.price_child === undefined
        ? Number(row.price_adult)
        : Number(row.price_child)
}

// Quote state + derived outputs for a package's booking widget. All quote rules live
// in `shared/quote.js`; this composable only wires package-specific fields (hotel
// tiers, adult/child prices, add-ons, deposit, price-basis wording) to them.
//
// `config` is the static Storyblok package content slice:
//   pricing           price-tier bloks: tier, season_start/end, price_adult, price_child
//   addons            package-addon bloks: label, price, unit (per_booking|per_person),
//                     default_selected — deliberately the same shape as tour extras
//   depositPercentage booking-deposit percentage (default 30)
//   priceBasis        'per_person' (default) | 'per_couple' — controls adult pricing
//   name, pageUrl     used in the WhatsApp summary + modal payload
//
// Returns reactive inputs (mutate directly from the widget's controls) and computed
// outputs; `bookingDetails` keeps tour's snake_case modal shape so one BookingModal
// serves both content types.
export function usePackageQuote(config) {
    const pricing = config.pricing || []
    const isCouple = config.priceBasis === 'per_couple'
    const addons = (config.addons || []).filter(addon => !isCouple || addon.unit !== 'per_person')
    const depositPercentage = config.depositPercentage === ''
        || config.depositPercentage === null
        || config.depositPercentage === undefined
        ? 30
        : Number(config.depositPercentage)
    const priceBasis = config.priceBasis || 'per_person'

    // --- Inputs (widget controls bind straight onto these) ---
    const tiers = [...new Set(pricing.map(row => row.tier).filter(Boolean))]
    const travelDate = ref('')
    const tier = ref(tiers[0] || '')
    const adults = ref(2)
    const children = ref(0)
    const selectedAddons = reactive(
        Object.fromEntries(addons.map(addon => [addon._uid, !!addon.default_selected])),
    )

    const totalGuests = computed(() => Number(adults.value) + Number(children.value))

    function setGuests(category, value) {
        if (isCouple) {
            adults.value = 2
            children.value = 0
            return
        }

        const normalized = Math.max(0, Math.round(Number(value) || 0))
        if (category === 'adults')
            adults.value = normalized
        else if (category === 'children')
            children.value = normalized
    }

    // Tier filter first, then the tour's season resolution over what remains.
    const selectedRow = computed(() =>
        resolveSeasonalRow(
            pricing.filter(row => !row.tier || row.tier === tier.value),
            travelDate.value,
        ),
    )

    const missingPrice = computed(() => {
        const row = selectedRow.value
        if (totalGuests.value === 0)
            return false
        if (!row)
            return true

        const adultPriceMissing = adults.value > 0 && !(Number(row.price_adult) > 0)
        const childPriceMissing = children.value > 0 && !(resolveChildPrice(row) > 0)
        return adultPriceMissing || childPriceMissing
    })

    const hasValidTravelDate = computed(() => {
        const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(travelDate.value)
        if (!match)
            return false

        const [, year, month, day] = match.map(Number)
        const date = new Date(Date.UTC(year, month - 1, day))
        return date.getUTCFullYear() === year
            && date.getUTCMonth() === month - 1
            && date.getUTCDate() === day
    })
    const canBook = computed(() =>
        totalGuests.value > 0 && hasValidTravelDate.value && !missingPrice.value,
    )

    // Why the quote cannot be booked yet, in the order a visitor would fix it. Lives here
    // rather than in the widget because every surface that renders a blocked call to
    // action — the sidebar and the dialog's footer — needs the same sentence.
    const bookingBlockReason = computed(() => {
        if (missingPrice.value)
            return 'No complete price is configured for these options. Ask our team for a custom quote.'
        if (totalGuests.value === 0)
            return 'Add at least one guest to continue.'
        if (!hasValidTravelDate.value)
            return 'Choose a valid travel date to continue.'
        return ''
    })

    const breakdown = computed(() => {
        const lines = []
        const row = selectedRow.value

        if (row) {
            const priceAdult = Number(row.price_adult) || 0
            const priceChild = resolveChildPrice(row) || 0
            if (adults.value > 0 && priceAdult) {
                const adultAmount = priceBasis === 'per_couple'
                    ? priceAdult
                    : adults.value * priceAdult
                const adultLabel = priceBasis === 'per_couple'
                    ? `Couple price $${priceAdult}`
                    : `Adults ${adults.value} x $${priceAdult}`
                lines.push({ label: adultLabel, amount: adultAmount })
            }
            if (children.value > 0 && priceChild)
                lines.push({ label: `Children ${children.value} x $${priceChild}`, amount: children.value * priceChild })
        }

        addons.forEach((addon) => {
            if (!selectedAddons[addon._uid])
                return
            const price = Number(addon.price)
            const amount = addon.unit === 'per_person' ? price * totalGuests.value : price
            lines.push({ label: `${addon.label}`, amount })
        })

        return lines
    })

    const total = computed(() => sumBreakdown(breakdown.value))
    const deposit = computed(() => Math.round((total.value * depositPercentage) / 100))

    // "From" price: lowest adult price across every tier/season, labelled per the
    // package's price basis.
    const fromPrice = computed(() => {
        const prices = pricing.map(row => Number(row.price_adult)).filter(price => price > 0)
        return prices.length ? Math.min(...prices) : null
    })
    const fromPriceLabel = computed(() =>
        fromPrice.value === null
            ? ''
            : `from $${fromPrice.value}${priceBasis === 'per_couple' ? '' : ' pp'}`,
    )

    // --- Booking flow (WhatsApp summary + modal payload) ---
    const guestsText = computed(() => {
        const parts = []
        if (adults.value > 0)
            parts.push(`${adults.value} adult${adults.value === 1 ? '' : 's'}`)
        if (children.value > 0)
            parts.push(`${children.value} child${children.value === 1 ? '' : 'ren'}`)
        return parts.join(', ') || 'No guests selected'
    })

    const addonsText = computed(() =>
        addons.filter(addon => selectedAddons[addon._uid]).map(addon => addon.label).join(', '),
    )
    const tierLabel = computed(() => tier.value ? formatPackageTier(tier.value) : '')
    const selectedAddonLabels = computed(() =>
        addons.filter(addon => selectedAddons[addon._uid]).map(addon => addon.label),
    )
    const packageOptionsText = computed(() => [
        tierLabel.value ? `Hotel tier: ${tierLabel.value}` : '',
        addonsText.value ? `Add-ons: ${addonsText.value}` : '',
    ].filter(Boolean).join('; '))

    const summaryText = computed(() => {
        const lines = [
            `Hi! I'd like to book: ${config.name}`,
            `Guests: ${guestsText.value}`,
        ]
        if (tierLabel.value)
            lines.push(`Hotel tier: ${tierLabel.value}`)
        if (travelDate.value)
            lines.push(`Travel date: ${travelDate.value}`)
        if (addonsText.value)
            lines.push(`Add-ons: ${addonsText.value}`)
        lines.push(`Estimated total: $${total.value}`)
        if (depositPercentage)
            lines.push(`Deposit (${depositPercentage}%): $${deposit.value}`)
        lines.push(config.pageUrl)
        return lines.join('\n')
    })

    const bookingDetails = computed(() => ({
        ...(config.itemUuid && { item_uuid: config.itemUuid }),
        tour: config.name,
        guests: totalGuests.value,
        guest_breakdown: { adults: adults.value, children: children.value },
        guests_text: guestsText.value,
        travel_date: travelDate.value,
        transfer_text: '',
        extras_text: packageOptionsText.value,
        summary_options: [
            tierLabel.value && { label: 'Hotel tier', value: tierLabel.value },
            selectedAddonLabels.value.length && { label: 'Add-ons', value: selectedAddonLabels.value },
        ].filter(Boolean),
        total: total.value,
        page_url: config.pageUrl,
    }))

    return {
        // inputs
        tiers,
        travelDate,
        tier,
        adults,
        children,
        selectedAddons,
        setGuests,
        isCouple,
        // quote outputs
        totalGuests,
        selectedRow,
        missingPrice,
        hasValidTravelDate,
        canBook,
        bookingBlockReason,
        breakdown,
        total,
        deposit,
        depositPercentage,
        fromPrice,
        fromPriceLabel,
        // booking flow
        summaryText,
        bookingDetails,
    }
}
